import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	Signal,
	SimpleChanges,
	viewChild,
} from '@angular/core'
import { NgTemplateOutlet } from '@angular/common'
import { TuiSkeleton } from '@taiga-ui/kit'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import { ThemeEnum } from '@modules/theme-module/enums/theme.enum'
import { PriceTargetInterface } from '@modules/twelve-data-module/interfaces/price-target.interface'

@Component({
	selector: 'app-tech-analysis',
	templateUrl: './tech-analysis.component.html',
	styleUrl: './tech-analysis.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NgTemplateOutlet, TuiSkeleton],
})
export class TechAnalysisComponent implements OnChanges, OnDestroy {
	protected readonly languageEnum: typeof LanguageEnum = LanguageEnum
	protected readonly themeEnum: typeof ThemeEnum = ThemeEnum

	public lang: InputSignal<LanguageEnum> = input<LanguageEnum>(this.languageEnum.ENGLISH)
	public theme: InputSignal<ThemeEnum> = input<ThemeEnum>(this.themeEnum.LIGHT)
	public priceTarget: InputSignal<PriceTargetInterface | null> = input<PriceTargetInterface | null>(null)
	public isLoadingPriceTarget: InputSignal<boolean> = input<boolean>(false)

	protected hasSymbol: Signal<boolean> = computed<boolean>((): boolean => !!this.priceTarget()?.meta?.symbol)

	private _techAnalysisEl: Signal<ElementRef<HTMLDivElement> | undefined> =
		viewChild<ElementRef<HTMLDivElement>>('techAnalysis')

	private _dropTechAnalysisEl(): void {
		const techAnalysisEl: ElementRef<HTMLDivElement> | undefined = this._techAnalysisEl()
		if (!techAnalysisEl) return
		techAnalysisEl.nativeElement.innerHTML = ''
	}

	private _loadTechAnalysis(): void {
		const techAnalysisEl: ElementRef<HTMLDivElement> | undefined = this._techAnalysisEl()
		if (!techAnalysisEl) return
		this._dropTechAnalysisEl()

		const priceTarget: PriceTargetInterface | null = this.priceTarget()
		const exchange: string | undefined = priceTarget?.meta?.exchange
		const symbol: string | undefined = priceTarget?.meta?.symbol
		if (!symbol) return

		const fullSymbol: string = exchange ? `${exchange}:${symbol}` : symbol

		const scriptEl: HTMLScriptElement = document.createElement('script')
		scriptEl.src = 'https://s3.tradingview.com/external-embedding/embed-widget-technical-analysis.js'
		scriptEl.async = true
		scriptEl.type = 'text/javascript'
		scriptEl.innerHTML = JSON.stringify({
			symbol: fullSymbol,
			interval: '1D',
			showIntervalTabs: true,
			displayMode: 'single',
			isTransparent: true,
			width: '100%',
			height: '100%',
			locale: this.lang(),
			colorTheme: this.theme(),
		})
		techAnalysisEl.nativeElement.appendChild(scriptEl)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.priceTarget()) {
			if (changes['lang'] || changes['theme'] || changes['priceTarget']) {
				this._loadTechAnalysis()
			}
		}
	}

	ngOnDestroy(): void {
		this._dropTechAnalysisEl()
	}
}
